import type { ReactNode } from 'react'
import { FileText, Info, Play, Square } from 'lucide-react'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { eventMeta } from '@/types/maintenanceTask'
import { useEventTypeCatalog } from '@/hooks/useEventTypes'
import { formatTime, SCHEDULE_STATUS_META, type ScheduleEvent } from '@/types/teamSchedule'
import type { AssignableUser } from '@/api/equipmentApi'
import { cn } from '@/lib/utils'
import { dayAndClock, formatDuration } from '@/lib/timeWindow'
import { reservationHours } from '@/lib/reservationTasks'

const UNASSIGNED = 'unassigned'

/** Add `hours` to an HH:mm time, wrapping within the day. */
function addHours(hhmm: string, hours: number): string {
  const [h, m] = hhmm.split(':').map(Number)
  const total = h * 60 + m + Math.round(hours * 60)
  const hh = Math.floor(total / 60) % 24
  const mm = total % 60
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='flex gap-3'>
      <span className='w-20 shrink-0 text-xs font-medium text-gray-400'>{label}</span>
      <span className='text-sm text-gray-800'>{children}</span>
    </div>
  )
}

export function ScheduleEventDialog({
  event,
  technicianName,
  dateLabel,
  assignableUsers,
  onAssign,
  onClose,
  onStart,
  onEnd,
}: {
  event: ScheduleEvent | null
  technicianName: string
  dateLabel: string
  assignableUsers: AssignableUser[]
  onAssign: (id: string, assigneeId: string) => void
  onClose: () => void
  onStart: (id: string) => void
  onEnd: (id: string) => void
}) {
  // The tenant's name for the type, never its code (z8nrz7fb9k).
  const catalog = useEventTypeCatalog()
  const evt = event ? eventMeta(event.eventType, catalog) : null
  const status = event ? SCHEDULE_STATUS_META[event.status] : null

  return (
    <Dialog open={event !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        {event && evt && status && (
          <>
            <div className='flex items-center gap-2.5'>
              <span className='h-8 w-1.5 shrink-0 rounded-full' style={{ backgroundColor: evt.color }} />
              <h2 className='text-base font-semibold text-gray-900'>{event.equipmentId}</h2>
            </div>

            <div className='mt-3 flex flex-wrap items-center gap-2'>
              <span
                className='inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium'
                style={{ backgroundColor: `${evt.color}14`, color: evt.color }}
              >
                <span className='h-1.5 w-1.5 rounded-full' style={{ backgroundColor: evt.color }} />
                {evt.label}
              </span>
              <span
                className='inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium'
                style={{ backgroundColor: `${status.color}14`, color: status.color }}
              >
                <span className='h-1.5 w-1.5 rounded-full' style={{ backgroundColor: status.color }} />
                {status.label}
              </span>
            </div>

            <div className='mt-4 space-y-2.5'>
              {event.readOnly && event.window ? (
                <>
                  {/* The real booked window, not timeStart + planHours: that
                      arithmetic wraps within a day and would misreport any hold
                      that crosses midnight. */}
                  <Row label='Booked'>
                    {dayAndClock(event.window.startsAt)} → {dayAndClock(event.window.endsAt)}
                    <span className='ml-1.5 text-xs text-gray-500'>
                      ({formatDuration(reservationHours(event.window.startsAt, event.window.endsAt))})
                    </span>
                  </Row>
                  <Row label='This day'>
                    {dateLabel} · {formatDuration(event.planHours)}
                  </Row>
                </>
              ) : (
                <Row label='When'>
                  {dateLabel} · {formatTime(event.timeStart)}–
                  {formatTime(addHours(event.timeStart, event.planHours))} ({event.planHours.toFixed(1)} h)
                </Row>
              )}
              {event.bookedFor && <Row label='For'>{event.bookedFor}</Row>}
              {event.readOnly ? (
                // No assignee picker: a booking is not assigned to a
                // technician, it is held by whoever reserved it.
                <Row label='Reserved by'>{event.bookedBy || technicianName || '—'}</Row>
              ) : (
              <Row label='Assigned'>
                {event.status === 'done' ? (
                  technicianName || 'Unassigned'
                ) : (
                  <Select
                    value={event.technicianId || UNASSIGNED}
                    onValueChange={(v) => onAssign(event.id, v === UNASSIGNED ? '' : v)}
                  >
                    <SelectTrigger className='h-8 w-[200px]'>
                      <SelectValue placeholder='Unassigned' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                      {assignableUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Row>
              )}
              <Row label='Location'>{event.location}</Row>
              {!event.readOnly && (
                <Row label='SOP'>
                  <span className='font-mono text-xs text-gray-700'>{event.sop}</span>
                </Row>
              )}
            </div>

            {/* A booking has no controls here at all: starting, ending and
                rescheduling belong to the process step that made it. */}
            {event.readOnly ? null : (
            <DialogFooter className='mt-6 sm:justify-start'>
              {event.status === 'scheduled' || event.status === 'overdue' ? (
                <button
                  type='button'
                  onClick={() => onStart(event.id)}
                  disabled={event.technicianId === UNASSIGNED}
                  title={
                    event.technicianId === UNASSIGNED ? 'Assign a technician before starting' : undefined
                  }
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors',
                    event.technicianId === UNASSIGNED
                      ? 'cursor-not-allowed bg-gray-300 shadow-none'
                      : 'bg-green-600 hover:bg-green-700 active:scale-[0.98]',
                  )}
                >
                  <Play className='h-4 w-4 fill-current' />
                  Start
                </button>
              ) : null}
              {event.status === 'started' ? (
                <button
                  type='button'
                  onClick={() => onEnd(event.id)}
                  className='inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3.5 py-2 text-sm font-semibold text-red-600 shadow-sm transition-colors hover:bg-red-50 active:scale-[0.98]'
                >
                  <Square className='h-4 w-4 fill-current' />
                  End
                </button>
              ) : (
                <span className='inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-3.5 py-2 text-sm font-medium text-gray-400'>
                  <Square className='h-4 w-4' />
                  End
                </span>
              )}
              <button
                type='button'
                title='Details'
                className='inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 hover:text-blue-600'
              >
                <Info className='h-4 w-4' />
              </button>
              <button
                type='button'
                title='Notes'
                className='inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 hover:text-blue-600'
              >
                <FileText className='h-4 w-4' />
              </button>
            </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
